/**
 * Oxirgi ochilganlar (shu qurilmada): driver va carrier profili ochilganda yoziladi (`recordVisit`), widget o'qiydi.
 * Brauzer xotirasi (`localStorage`) -- brauzer tarixi kabi shaxsiy; serverga yuborilmaydi (test: store.test.ts).
 */
import { useSyncExternalStore } from "react";

export type RecentKind = "driver" | "carrier";

export interface RecentEntry {
  kind: RecentKind;
  id: number;
  title: string;
  subtitle: string;
  /** ISO vaqt */
  at: string;
}

export const RECENT_KEY = "dh.recent";
export const RECENT_MAX = 10;

type Storage = Pick<globalThis.Storage, "getItem" | "setItem">;

function safeStorage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

export function readRecent(storage: Storage | null = safeStorage()): RecentEntry[] {
  try {
    const raw = storage?.getItem(RECENT_KEY);
    const list = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(list)
      ? list.filter((e): e is RecentEntry => !!e && typeof e === "object" && (e as RecentEntry).kind !== undefined && typeof (e as RecentEntry).id === "number" && typeof (e as RecentEntry).title === "string")
      : [];
  } catch {
    return [];
  }
}

/** Yangi tashrif boshiga, takrori olib tashlanadi, eng ko'pi RECENT_MAX */
export function pushRecent(list: RecentEntry[], entry: RecentEntry, max = RECENT_MAX): RecentEntry[] {
  return [entry, ...list.filter((e) => !(e.kind === entry.kind && e.id === entry.id))].slice(0, max);
}

let cache: RecentEntry[] | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

function current(): RecentEntry[] {
  if (cache === null) cache = readRecent();
  return cache;
}

export function recordVisit(entry: Omit<RecentEntry, "at">) {
  const next = pushRecent(current(), { ...entry, at: new Date().toISOString() });
  cache = next;
  try {
    safeStorage()?.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    /* maxfiy rejim: faqat xotirada */
  }
  notify();
}

if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key === RECENT_KEY) {
      cache = readRecent();
      notify();
    }
  });
}

export function useRecent(): RecentEntry[] {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    current,
    () => [],
  );
}
